import { useState, useCallback } from 'react';
import GoogleMapService from '../../../services/osb-service/google-map-service/google-map-service';

interface LocationError {
    type:
        | 'PermissionDenied'
        | 'PositionUnavailable'
        | 'Timeout'
        | 'GeocodingFailed'
        | 'UnsupportedBrowser'
        | 'Unknown';
    message: string;
}

interface GeolocationOptions {
    timeout?: number;
    maximumAge?: number;
    enableHighAccuracy?: boolean;
}

export const useLocationAddress = (options: GeolocationOptions = {}) => {
    const [address, setAddress] = useState<string | null>(null);
    const [error, setError] = useState<LocationError | null>(null);
    const [isLoading, setIsLoading] = useState(false);

    const getAddress = useCallback(() => {
        setIsLoading(true);
        setAddress(null);
        setError(null);

        if (!navigator.geolocation) {
            setError({
                type: 'UnsupportedBrowser',
                message: 'Geolocation is not supported.',
            });
            setIsLoading(false);
            return;
        }
        // eslint-disable-next-line
        navigator.geolocation.getCurrentPosition(
            position => {
                GoogleMapService.getReverseGeoCoding(
                    position.coords.latitude,
                    position.coords.longitude,
                    (data: any) => {
                        if (data) {
                            setAddress(data);
                        } else {
                            setError({
                                type: 'GeocodingFailed',
                                message:
                                    'Could not find an address for the location.',
                            });
                        }
                        setIsLoading(false);
                    }
                );
            },
            err => {
                let errorType: LocationError['type'] = 'Unknown';
                if (err.code === 1) errorType = 'PermissionDenied';
                if (err.code === 2) errorType = 'PositionUnavailable';
                if (err.code === 3) errorType = 'Timeout';

                setError({ type: errorType, message: err.message });
                setIsLoading(false);
            },
            options
        );
    }, [options]);

    const clearError = () => {
        setError(null);
    };
    return { address, error, isLoading, getAddress, clearError };
};
